package com.example.hukakmp.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.model.TournamentParticipantStatus
import com.example.hukakmp.model.TournamentReport
import com.example.hukakmp.model.TournamentReportStatus
import com.example.hukakmp.model.TournamentScoreType
import com.example.hukakmp.model.TournamentStatus
import com.example.hukakmp.model.TournamentSummary
import com.example.hukakmp.model.scoreTypeEnum
import com.example.hukakmp.model.status
import com.example.hukakmp.util.fechaHoyDMY
import com.example.hukakmp.util.formatearFechaDMY
import com.example.hukakmp.viewmodel.TournamentViewModel
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

@Composable
fun TournamentsScreen(
    uiState: TournamentViewModel.UiState,
    onRefresh: () -> Unit,
    onCreate: (String, String, String, String, TournamentScoreType) -> Unit,
    onSearch: (String) -> Unit,
    onJoin: (String) -> Unit,
    onRespond: (String, String, Boolean) -> Unit,
    onRejectReport: (String, String, String) -> Unit,
    onDismissCreatedCode: () -> Unit,
    onDismissFound: () -> Unit,
    onClearRequestSent: () -> Unit,
    onClearError: () -> Unit,
    onBack: () -> Unit,
) {
    var showCreate by remember { mutableStateOf(false) }
    var showJoin by remember { mutableStateOf(false) }

    uiState.createdCode?.let { code ->
        AlertDialog(
            onDismissRequest = onDismissCreatedCode,
            icon = { Text("🏆", fontSize = 34.sp) },
            title = { Text("Torneo creado") },
            text = {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        "Compartí este código para que otros pescadores soliciten unirse:",
                        textAlign = TextAlign.Center,
                    )
                    Surface(
                        modifier = Modifier.padding(top = 14.dp),
                        shape = RoundedCornerShape(14.dp),
                        color = MaterialTheme.colorScheme.primaryContainer,
                    ) {
                        Text(
                            code,
                            modifier = Modifier.padding(horizontal = 24.dp, vertical = 12.dp),
                            fontSize = 26.sp,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onPrimaryContainer,
                        )
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = onDismissCreatedCode) { Text("Listo") }
            },
        )
    }

    if (showCreate) {
        CreateTournamentDialog(
            isLoading = uiState.isLoading,
            onDismiss = { showCreate = false },
            onCreate = { name, description, start, end, type ->
                onCreate(name, description, start, end, type)
                showCreate = false
            },
        )
    }

    if (showJoin) {
        JoinTournamentDialog(
            uiState = uiState,
            onSearch = onSearch,
            onJoin = { id ->
                onJoin(id)
                showJoin = false
            },
            onDismiss = {
                onDismissFound()
                onClearError()
                showJoin = false
            },
        )
    }

    if (uiState.requestSent) {
        AlertDialog(
            onDismissRequest = onClearRequestSent,
            icon = { Text("🎣", fontSize = 32.sp) },
            title = { Text("Solicitud enviada") },
            text = {
                Text("El organizador debe aceptarte antes de que puedas participar.")
            },
            confirmButton = {
                TextButton(onClick = onClearRequestSent) { Text("Entendido") }
            },
        )
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            TopAppBar(
                navigationIcon = {
                    TextButton(onClick = onBack) {
                        Text("‹", color = MaterialTheme.colorScheme.onPrimary, fontSize = 30.sp)
                    }
                },
                title = {
                    Column {
                        Text("Torneos", fontWeight = FontWeight.Bold)
                        Text(
                            "Competí con otros pescadores",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.76f),
                        )
                    }
                },
                actions = {
                    TextButton(onClick = onRefresh) {
                        Text("↻", color = MaterialTheme.colorScheme.onPrimary, fontSize = 22.sp)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    titleContentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            )
        },
    ) { padding ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    OutlinedButton(
                        onClick = { showJoin = true },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(14.dp),
                    ) {
                        Text("Unirme con código")
                    }
                    Button(
                        onClick = { showCreate = true },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(14.dp),
                    ) {
                        Text("+ Crear torneo")
                    }
                }
            }

            uiState.error?.let { error ->
                item {
                    Surface(
                        modifier = Modifier.fillMaxWidth().clickable(onClick = onClearError),
                        shape = RoundedCornerShape(14.dp),
                        color = MaterialTheme.colorScheme.errorContainer,
                    ) {
                        Text(
                            error,
                            modifier = Modifier.padding(14.dp),
                            color = MaterialTheme.colorScheme.onErrorContainer,
                        )
                    }
                }
            }

            if (uiState.isLoading && uiState.tournaments.isEmpty()) {
                item {
                    Box(
                        modifier = Modifier.fillMaxWidth().height(180.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        CircularProgressIndicator()
                    }
                }
            } else if (uiState.tournaments.isEmpty()) {
                item {
                    Surface(
                        modifier = Modifier.fillMaxWidth().height(220.dp),
                        shape = RoundedCornerShape(22.dp),
                        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.42f),
                    ) {
                        Column(
                            modifier = Modifier.fillMaxSize().padding(24.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center,
                        ) {
                            Text("🏆", fontSize = 56.sp)
                            Text(
                                "Sumate a la competencia",
                                fontWeight = FontWeight.Bold,
                                fontSize = 20.sp,
                            )
                            Text(
                                "Creá un torneo entre amigos o ingresá el código que te compartieron.",
                                textAlign = TextAlign.Center,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(top = 6.dp),
                            )
                        }
                    }
                }
            } else {
                item {
                    Text(
                        "Mis torneos",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                    )
                }

                items(uiState.tournaments, key = { it.tournament.id }) { summary ->
                    TournamentCard(
                        summary = summary,
                        onRespond = { participantId, accept ->
                            onRespond(summary.tournament.id, participantId, accept)
                        },
                        onRejectReport = { reportId, reason ->
                            onRejectReport(summary.tournament.id, reportId, reason)
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun TournamentCard(
    summary: TournamentSummary,
    onRespond: (String, Boolean) -> Unit,
    onRejectReport: (String, String) -> Unit,
) {
    val tournament = summary.tournament
    var reportToReject by remember { mutableStateOf<TournamentReport?>(null) }
    var rejectReason by remember { mutableStateOf("") }

    reportToReject?.let { report ->
        AlertDialog(
            onDismissRequest = {
                reportToReject = null
                rejectReason = ""
            },
            title = { Text("Rechazar parte") },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text(
                        "Se restarán ${report.puntaje} puntos a ${report.userName}.",
                        style = MaterialTheme.typography.bodyMedium,
                    )
                    OutlinedTextField(
                        value = rejectReason,
                        onValueChange = { rejectReason = it },
                        label = { Text("Motivo (opcional)") },
                        modifier = Modifier.fillMaxWidth(),
                        maxLines = 3,
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        onRejectReport(report.parteId, rejectReason)
                        reportToReject = null
                        rejectReason = ""
                    },
                ) {
                    Text("Rechazar")
                }
            },
            dismissButton = {
                TextButton(
                    onClick = {
                        reportToReject = null
                        rejectReason = ""
                    },
                ) {
                    Text("Cancelar")
                }
            },
        )
    }
    val state = tournament.status()
    val stateColor = when (state) {
        TournamentStatus.PROXIMO -> Color(0xFF378ADD)
        TournamentStatus.ACTIVO -> Color(0xFF0891B2)
        TournamentStatus.FINALIZADO -> MaterialTheme.colorScheme.outline
    }
    val stateLabel = when (state) {
        TournamentStatus.PROXIMO -> "Próximo"
        TournamentStatus.ACTIVO -> "En curso"
        TournamentStatus.FINALIZADO -> "Finalizado"
    }

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 2.dp,
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        tournament.nombre,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                    )
                    Text(
                        "${formatTournamentDate(tournament.fechaInicio)} → ${formatTournamentDate(tournament.fechaFin)}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Surface(
                    color = stateColor.copy(alpha = 0.15f),
                    shape = RoundedCornerShape(30.dp),
                ) {
                    Text(
                        stateLabel,
                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                        color = stateColor,
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.labelSmall,
                    )
                }
            }

            if (tournament.descripcion.isNotBlank()) {
                Text(
                    tournament.descripcion,
                    modifier = Modifier.padding(top = 10.dp),
                    style = MaterialTheme.typography.bodyMedium,
                )
            }

            Row(
                modifier = Modifier.padding(top = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(14.dp),
            ) {
                Text(
                    "🎯 ${tournament.scoreTypeEnum().displayName}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    "👥 ${summary.accepted.size}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            if (summary.isCreator) {
                Surface(
                    modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.55f),
                    shape = RoundedCornerShape(12.dp),
                ) {
                    Column(modifier = Modifier.padding(12.dp)) {
                        Text(
                            "Organizador · Código",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onPrimaryContainer,
                        )
                        Text(
                            tournament.codigoInvitacion,
                            fontSize = 21.sp,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary,
                        )
                    }
                }

                if (summary.pending.isNotEmpty()) {
                    Text(
                        "Solicitudes pendientes",
                        modifier = Modifier.padding(top = 14.dp, bottom = 5.dp),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.tertiary,
                    )
                    summary.pending.forEach { participant ->
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(participant.userName, modifier = Modifier.weight(1f))
                            TextButton(onClick = { onRespond(participant.userId, false) }) {
                                Text("Rechazar", color = MaterialTheme.colorScheme.error)
                            }
                            Button(
                                onClick = { onRespond(participant.userId, true) },
                                shape = RoundedCornerShape(10.dp),
                            ) {
                                Text("Aceptar")
                            }
                        }
                    }
                }
            } else {
                val (label, color) = when (summary.myStatus) {
                    TournamentParticipantStatus.PENDIENTE ->
                        "⏳ Solicitud pendiente" to MaterialTheme.colorScheme.tertiary
                    TournamentParticipantStatus.ACEPTADO ->
                        "✅ Participando · Puesto #${summary.myPosition}" to MaterialTheme.colorScheme.secondary
                    TournamentParticipantStatus.RECHAZADO ->
                        "❌ Solicitud rechazada" to MaterialTheme.colorScheme.error
                    null -> "" to MaterialTheme.colorScheme.onSurfaceVariant
                }
                if (label.isNotBlank()) {
                    Text(
                        label,
                        modifier = Modifier.padding(top = 12.dp),
                        color = color,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }

            if (summary.accepted.isNotEmpty()) {
                Text(
                    "Leaderboard",
                    modifier = Modifier.padding(top = 14.dp, bottom = 4.dp),
                    fontWeight = FontWeight.Bold,
                )
                summary.accepted.take(5).forEachIndexed { index, participant ->
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp),
                    ) {
                        Text(
                            when (index) {
                                0 -> "🥇"
                                1 -> "🥈"
                                2 -> "🥉"
                                else -> "${index + 1}."
                            },
                            modifier = Modifier.padding(end = 8.dp),
                        )
                        Text(participant.userName, modifier = Modifier.weight(1f))
                        Text(
                            "${participant.puntaje} pts",
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary,
                        )
                    }
                }
            }

            if (summary.reports.isNotEmpty()) {
                Text(
                    if (summary.isCreator) "Partes del torneo" else "Mis partes en el torneo",
                    modifier = Modifier.padding(top = 16.dp, bottom = 6.dp),
                    fontWeight = FontWeight.Bold,
                )

                summary.reports
                    .sortedByDescending { it.creadoEn.seconds }
                    .take(8)
                    .forEach { report ->
                        val rejected = report.estado == TournamentReportStatus.RECHAZADO.name
                        Surface(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                            shape = RoundedCornerShape(12.dp),
                            color = if (rejected) {
                                MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.45f)
                            } else {
                                MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.55f)
                            },
                        ) {
                            Column(modifier = Modifier.padding(11.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(
                                            report.userName,
                                            fontWeight = FontWeight.SemiBold,
                                            style = MaterialTheme.typography.bodyMedium,
                                        )
                                        Text(
                                            report.fecha.ifBlank { "Fecha sin informar" },
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        )
                                    }
                                    Text(
                                        if (rejected) "Rechazado" else "+${report.puntaje} pts",
                                        fontWeight = FontWeight.Bold,
                                        color = if (rejected) {
                                            MaterialTheme.colorScheme.error
                                        } else {
                                            MaterialTheme.colorScheme.primary
                                        },
                                    )
                                }

                                if (report.especies.isNotEmpty()) {
                                    Text(
                                        report.especies.joinToString(" · ") {
                                            "${it.nombre} ×${it.cantidad}"
                                        },
                                        modifier = Modifier.padding(top = 5.dp),
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    )
                                } else {
                                    Text(
                                        "Sin capturas",
                                        modifier = Modifier.padding(top = 5.dp),
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    )
                                }

                                if (rejected && report.motivoRechazo.isNotBlank()) {
                                    Text(
                                        "Motivo: ${report.motivoRechazo}",
                                        modifier = Modifier.padding(top = 5.dp),
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.error,
                                    )
                                }

                                if (summary.isCreator && !rejected) {
                                    TextButton(
                                        onClick = {
                                            reportToReject = report
                                            rejectReason = ""
                                        },
                                        modifier = Modifier.align(Alignment.End),
                                    ) {
                                        Text(
                                            "Rechazar parte",
                                            color = MaterialTheme.colorScheme.error,
                                        )
                                    }
                                }
                            }
                        }
                    }
            }
        }
    }
}

@Composable
private fun CreateTournamentDialog(
    isLoading: Boolean,
    onDismiss: () -> Unit,
    onCreate: (String, String, String, String, TournamentScoreType) -> Unit,
) {
    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var startDate by remember { mutableStateOf(fechaHoyDMY()) }
    var endDate by remember { mutableStateOf(fechaHoyDMY()) }
    var type by remember { mutableStateOf(TournamentScoreType.CANTIDAD_PECES) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Crear torneo") },
        text = {
            Column(
                modifier = Modifier.verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Nombre") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = { Text("Descripción") },
                    modifier = Modifier.fillMaxWidth(),
                    maxLines = 3,
                )
                OutlinedTextField(
                    value = startDate,
                    onValueChange = { startDate = it },
                    label = { Text("Inicio (dd/MM/yyyy)") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = endDate,
                    onValueChange = { endDate = it },
                    label = { Text("Fin (dd/MM/yyyy)") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                )
                Text("Puntaje", fontWeight = FontWeight.Bold)
                TournamentScoreType.entries.forEach { item ->
                    FilterChip(
                        selected = type == item,
                        onClick = { type = item },
                        label = { Text(item.displayName) },
                    )
                }
                if (type == TournamentScoreType.PERSONALIZADO) {
                    Text(
                        "La edición de reglas personalizadas se agregará en el siguiente bloque. Por ahora podés crear el torneo y configurarlo luego desde Android.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onCreate(name, description, startDate, endDate, type) },
                enabled = !isLoading && name.trim().length >= 3,
            ) {
                Text("Crear")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancelar") }
        },
    )
}

@Composable
private fun JoinTournamentDialog(
    uiState: TournamentViewModel.UiState,
    onSearch: (String) -> Unit,
    onJoin: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    var code by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Unirme a un torneo") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = code,
                    onValueChange = { code = it.uppercase() },
                    label = { Text("Código de invitación") },
                    placeholder = { Text("HUKA-XXXXXX") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                )

                uiState.foundTournament?.let { tournament ->
                    Surface(
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(14.dp),
                        color = MaterialTheme.colorScheme.primaryContainer,
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Text(tournament.nombre, fontWeight = FontWeight.Bold)
                            Text(
                                "Organiza: ${tournament.creatorName}",
                                style = MaterialTheme.typography.bodySmall,
                            )
                            if (tournament.descripcion.isNotBlank()) {
                                Text(
                                    tournament.descripcion,
                                    style = MaterialTheme.typography.bodySmall,
                                    modifier = Modifier.padding(top = 5.dp),
                                )
                            }
                            Button(
                                onClick = { onJoin(tournament.id) },
                                modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                            ) {
                                Text("Solicitar unirme")
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            if (uiState.foundTournament == null) {
                Button(
                    onClick = { onSearch(code) },
                    enabled = code.trim().isNotBlank() && !uiState.isLoading,
                ) {
                    if (uiState.isLoading) {
                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    } else {
                        Text("Buscar")
                    }
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cerrar") }
        },
    )
}

private fun formatTournamentDate(timestamp: Timestamp): String {
    val zone = TimeZone.currentSystemDefault()
    val date = Instant.fromEpochSeconds(timestamp.seconds, timestamp.nanoseconds.toLong())
        .toLocalDateTime(zone)
        .date
    return formatearFechaDMY(date)
}
